// src/pages/About.jsx
import React from "react";
import { BookOpen, Users, GraduationCap, User } from "lucide-react";
import dnaImg from "../images/dna_banner.jpg";
import workflowSvg from "../images/workflow.pptx.svg";
import graphicsSvg from "../images/Slide1.jpg";
import Footer from "../components/Footer";

const About = () => {
  return (
    <>
      <main className="page about-page min-h-[80vh] py-16 px-6 md:px-16 bg-white">
        <section className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8 items-start mb-12">
            <div className="md:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                About / Manual
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-full bg-emerald-100">
                  <BookOpen className="w-5 h-5 text-emerald-700" />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
                  About{" "}
                  <span className="text-emerald-700">Ori</span>
                  <span className="text-purple-500">genomi</span>
                </h1>
              </div>
              <p className="text-sm md:text-base leading-relaxed text-slate-700 max-w-3xl text-justify">
                Accurate, high-quality genome sequences are essential for
                reliable prediction of genomic features using computational
                methods. In practice, circular bacterial genomes often carry
                assembly artifacts as redundant sequence at the termini is not
                properly truncated. Such errors can falsely inflate the copy
                number of genes located near the sequence ends. In addition, the
                absence of a standardized start position for bacterial genomes
                complicates comparative and syntenic analyses.
              </p>
            </div>

            {/* <div className="md:col-span-2">
              <img
                src={dnaImg}
                alt="DNA double helix"
                className="w-full h-full max-h-80 object-cover rounded-3xl shadow-lg"
              />
            </div> */}
          </div>

          {/* BLOCK 2: MAIN CONTENT */}
          <div className="space-y-8 text-slate-800">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-semibold mb-3">
                What Origenomi does
              </h2>
              <div className="space-y-4 text-sm md:text-base leading-relaxed text-slate-700">
                <p className="text-justify">
                  <strong>Origenomi</strong> addresses these challenges by
                  identifying and removing redundant end sequences and by
                  reorienting genomes to begin at the{" "}
                  <strong>OriC/dnaA</strong> locus, thereby providing a more
                  consistent and biologically meaningful representation and a
                  high quality genome.
                </p>
                <p className="text-justify">
                  Origenomi is available as a user-friendly web server that
                  accepts genomic <strong>FASTA</strong> files,{" "}
                  <strong>GenBank</strong> files, or directly retrieves genomes
                  using an <strong>NCBI accession ID</strong>.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Terminal redundancy correction
                </h3>
                <p className="text-sm md:text-base leading-relaxed text-slate-700 text-justify">
                  To identify terminal redundancies in circular assemblies, the
                  tool divides each sequence into two segments and performs a{" "}
                  <strong>blastn</strong> comparison between them. When
                  overlapping or duplicated regions are detected, one of the
                  redundant portions is trimmed to produce a single better-quality
                  chromosome.
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  OriC and dnaA localization &amp; reorientation
                </h3>
                <p className="text-sm md:text-base leading-relaxed text-slate-700 text-justify">
                  Following correction, Origenomi searches for the{" "}
                  <strong>OriC</strong> and <strong>dnaA</strong> loci using{" "}
                  <strong>blastn</strong> against the <strong>DoriC v12</strong>{" "}
                  database and an in-house curated <strong>dnaA</strong> database,
                  respectively. All OriC–dnaA pairs are evaluated and the most
                  plausible combination is selected based on interlocus distance
                  and local GC percentage variation. The genome is then
                  reoriented so that the chosen OriC–dnaA region marks the start
                  of the sequence.
                </p>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Draft assemblies &amp; multiple replicons
              </h3>
              <p className="text-sm md:text-base leading-relaxed text-slate-700 text-justify">
                For genomes with multiple replicons or for draft assemblies, each
                sequence is processed independently using the same workflow.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 items-start">
              <figure className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm">
                <img
                  src={workflowSvg}
                  alt="Origenomi workflow"
                  className="w-full h-auto rounded-xl"
                />
                <figcaption className="mt-3 text-xs md:text-sm text-slate-600 text-justify">
                  Workflow overview: redundancy trimming, OriC/dnaA search, and
                  reorientation.
                </figcaption>
              </figure>

              <figure className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 shadow-sm">
                <img
                  src={graphicsSvg}
                  alt="Origenomi outputs and summary figures"
                  className="w-full h-auto rounded-xl"
                />
                <figcaption className="mt-3 text-xs md:text-sm text-slate-600 text-justify">
                  Outputs: corrected genome FASTA plus comparison data and
                  summary figures (dot-plot and Mauve-based synteny
                  visualization).
                </figcaption>
              </figure>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-semibold mb-2">
                Final outputs
              </h2>
              <p className="text-sm md:text-base leading-relaxed text-slate-700 text-justify">
                The final output includes a corrected genomic FASTA file as well
                as comparison data and summary figures including a dot-plot and
                Mauve-based synteny visualization illustrating the differences
                between the input genome and its corrected high-quality version.
              </p>
            </div>

            {/* Team & Acknowledgement – pretty card, Sprih-style inspiration */}
            <div className="mt-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                Team &amp; Acknowledgement
              </div>

              <div className="space-y-6">
                {/* Project Team */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-emerald-100">
                      <Users className="w-4 h-4 text-emerald-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      Project Team
                    </h3>
                  </div>
                  <p className="text-sm md:text-base leading-relaxed text-slate-700 mb-2 text-justify">
                    Origenomi has been conceptualized, designed, and
                    implemented by SharmaG_omics Lab from the{" "}
                    Department of Biotechnology, IIT Hyderabad.
                  </p>

                  {/* Team Members Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {[
                      { name: "Utkarsha Mahanta", role: "PhD Scholar" },
                      { name: "Patil Anant Mohan", role: "B.Tech Student" },
                      { name: "Kankariya Aditya Rajesh", role: "B.Tech Student" },
                      { name: "Gyanesh Shukla", role: "PhD Scholar" },
                      { name: "Jiya Garg", role: "Intern" },
                      { name: "Dr. Gaurav Sharma", role: "Group Lead" },
                    ].map((member) => (
                      <div
                        key={member.name}
                        className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center text-center"
                      >
                        <div className="w-12 h-12 rounded-full bg-emerald-100 border border-emerald-200 flex items-center justify-center mb-2">
                          <User className="w-5 h-5 text-emerald-700" />
                        </div>
                        <h4 className="text-sm font-semibold text-slate-900">
                          {member.name}
                        </h4>
                        <p className="text-xs text-slate-600 mt-0.5">{member.role}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Acknowledgement 
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-blue-100">
                      <GraduationCap className="w-4 h-4 text-blue-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      Acknowledgement
                    </h3>
                  </div>
                  <p className="text-sm md:text-base leading-relaxed text-slate-700 text-justify">
                    We gratefully acknowledge the guidance, feedback, and
                    continuous support from our lab
                    members at IIT Hyderabad. Their inputs were invaluable in
                    shaping this tool—from refining prediction logic to
                    validating results on real-world datasets.
                  </p>
                  <p className="text-sm md:text-base leading-relaxed text-slate-700 mt-3 text-justify">
                    We also thank the broader research community whose
                    publicly available bacterial genome datasets and tools
                    inspired the design of Origenomi and enabled benchmarking
                    of our predictions.
                  </p>
                </div> */}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer on About page */}
      <Footer />
    </>
  );
};

export default About;
